import { useCart } from '@shared/cart-react';
import { Button, cn, SmoothMount } from '@shared/cove-ui';
import { useProducts } from '@shared/react-product';
import { useMemo, useState } from 'react';
import { Monitoring, PromoCodeBreakdown, PromoCodeInput } from '.';
import { roundTo } from '../../../helpers/tools';
import PrettyPrice from '../PrettyPrice';
import Price from '../Price';

export default function CartTotalsCollapsible({
  useOrder,
}: {
  useOrder: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const cart = useCart();
  const products = useProducts();

  // Just show equipment
  const exclude = [
    'window-sticker-sm',
    'vm-pn',
    'CP-YS-23i-1',
    'bm-pn',
    'bm-pl',
    'vm-pl',
    'vm-gp',
  ];

  const equipment = useMemo(() => {
    return Object.values(cart.cartProducts).filter(
      product => !exclude.includes(product.sku)
    );
  }, [cart.cartProducts]);

  const panel = equipment?.find(product => product.sku === 'CPA-PANEL-345-1');
  const hub = equipment?.find(product => product.sku === 'CPA-HUB-345-1');

  // --- Put panel and hub at the top ---
  const orderedEquipment = [...equipment]?.filter(
    product =>
      product.sku !== 'CPA-PANEL-345-1' && product.sku !== 'CPA-HUB-345-1'
  );

  if (panel && hub) {
    orderedEquipment.unshift(hub, panel);
  }

  const totals = cart?.totals?.totals || {
    orderGrandTotal: 0,
    orderRetailTotal: 0,
    orderTotalWithTax: 0,
    equipmentTotalTax: 0,
    equipmentBaseTotal: 0,
    allEquipmentGrandTotal: 0,
    equipmentGrandTotal: 0,
    equipmentRetailTotal: 0,
    equipmentTotalWithTax: 0,
    monitoringTotalWithTax: 0,
    monitoringRetailTotalTax: 0,
    payLaterBalanceWithTax: 0,
  };

  const isTaxCalculated = cart?.taxCalculated || false;
  const totalDiscount = totals?.orderRetailTotal - totals?.orderTotalWithTax;
  const hasAffirmTransaction =
    typeof cart?.affirmAmount === 'number' && cart?.affirmAmount > 0;

  return (
    <div className="group" data-expanded={expanded}>
      <div className="flex w-full items-center justify-between font-semibold">
        <Button variant="text" onClick={() => setExpanded(!expanded)}>
          <span className="text-cove-navy-blue text-lg font-semibold">
            Order Summary
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-2 transition-transform duration-300 ease-in-out group-data-[expanded=true]:rotate-180"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </Button>

        <PrettyPrice
          price={
            cart?.getTodaysGrandTotal?.() || totals?.orderTotalWithTax || 0
          }
        />
      </div>
      <SmoothMount show={expanded} showOverflow>
        <div className="mb-cove-5 pt-cove-10 flex items-center justify-between">
          <div>
            <span className="xs:items-center xs:flex-row xs:gap-cove-5 flex flex-col items-start gap-0">
              <h5 className="text-h5">Equipment</h5>
              <SmoothMount height={false} fade show={isTaxCalculated}>
                <span className="text-cove-navy-blue m-0 text-xs">
                  Including tax of: ${roundTo(totals.equipmentTotalTax)}
                </span>
              </SmoothMount>
            </span>
          </div>
          <div aria-hidden="true" className="flex items-start">
            <Price
              price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
              salePrice={parseFloat(`${totals.equipmentTotalWithTax || 0}`)}
            />
          </div>
          <p className="sr-only">{`$${roundTo(
            totals.equipmentGrandTotal
          )}, regularly $${roundTo(totals.equipmentBaseTotal)}`}</p>
        </div>
        {orderedEquipment?.map((product, i) => {
          const { sku, qty, name } = product;
          return (
            <div
              key={product.sku}
              className={cn(
                'border-cove-dove py-cove-5 px-cove-2 flex w-full justify-between',
                i !== 0 && 'border-t'
              )}
            >
              <div className="gap-cove-15 flex items-center">
                <span className="flex h-[45px] w-[45px] items-center justify-center">
                  <img
                    className="max-w-[45px]"
                    src={products[sku].primaryImage?.url}
                    alt={name}
                  />
                </span>
                <p className="text-cove-navy-blue text-base">{name}</p>
              </div>
              <p className="text-cove-navy-blue self-center text-base">{qty}</p>
            </div>
          );
        })}
        <Monitoring useOrder={useOrder} />
        <div className="mb-cove-15 flex items-center justify-between">
          <div className="gap-cove-5 flex items-center">
            <h5 className="text-h5">Shipping</h5>
            <span className="text-cove-navy-blue m-0 text-xs">
              2 - 7 business days
            </span>
          </div>
          <p className="text-cove-green ml-cove-5 text-right font-semibold">
            Free
          </p>
        </div>
        <PromoCodeBreakdown />
        <div className="mb-cove-15 flex items-center justify-between">
          <h5 className="text-h5">Today’s Savings</h5>
          <span className="text-cove-red">
            -<PrettyPrice price={totalDiscount || 0} />
          </span>
        </div>
        <div className="border-t-cove-dove pt-cove-15 my-cove-15 [&_.sale-price]:text-cove-green flex items-center justify-between border-t">
          <h2 className="text-h5">Order Total</h2>
          <Price
            price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
            salePrice={
              cart?.getTodaysGrandTotal?.() || totals.orderTotalWithTax || 0
            }
          />
        </div>
        {hasAffirmTransaction && (
          <div className="mb-cove-15 flex items-center justify-between">
            <h2 className="text-h5">Affirm</h2>
            <div className="flex items-center">
              -<PrettyPrice price={cart?.affirmAmount || 0} />
            </div>
          </div>
        )}
        <PromoCodeInput />
        <div className="pb-cove-35" />
      </SmoothMount>
    </div>
  );
}
